import { isNil } from '@flowio/is';
import isEqual from 'lodash/isEqual';
import fetchInternalPaymentRequest from '../services/paymentRequest/fetchInternalPaymentRequest';
import fetchPaymentRequest from '../services/paymentRequest/fetchPaymentRequest';
import { DomReference, PaymentRequestCallbackQuery } from '../types';
import { createInlineWindowElements } from '../utilities/createActionDomElements';
import createInlineCallbackListener from '../utilities/createInlineCallbackListener';
import pollForPrUpdate from '../utilities/pollForPrUpdate';
import { resolveElement, removeElement } from '../utilities/elementHelpers';
import isInternalPaymentRequest from '../validators/isInternalPaymentRequest';

function getIframeStyles(
  action: io.flow.v0.models.ActionDisplayInlineWindow,
): Record<string, string> {
  switch (action.window_size) {
    case 'xxx_small': return { width: '250px', height: '400px' };
    case 'xx_small': return { width: '390px', height: '400px' };
    case 'x_small': return { width: '500px', height: '600px' };
    case 'small': return { width: '600px', height: '400px' };
    case 'responsive': return { width: '100%', height: '100%' };
    default: return { // fullscreen
      position: 'fixed',
      backgroundColor: 'white',
      top: '0',
      left: '0',
      bottom: '0',
      right: '0',
      width: '100%',
      height: '100%',
    };
  }
}

export default function handleDisplayInlineWindowAction(
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  action: io.flow.v0.models.ActionDisplayInlineWindow,
  container: DomReference,
): Promise<io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest> {
  const resolvedContainer = resolveElement(container);
  if (isNil(resolvedContainer)) {
    // eslint-disable-next-line no-console
    console.error('handleDisplayInlineWindowAction: Mounting container was not set.');
    return Promise.reject(new Error('handleDisplayInlineWindowAction: Mounting container was not set.'));
  }

  const callbackPromise = createInlineCallbackListener<PaymentRequestCallbackQuery>({
    expiresAt: action.expires_at,
  });

  let cancelPolling: () => void;

  const pollingPromise = new Promise<
  io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest
  >((resolve, reject) => {
    cancelPolling = pollForPrUpdate(pr, {
      diffCheck: (pr1, pr2) => !isEqual(
        isInternalPaymentRequest(pr1) ? pr1.actual_action : pr1.next_action,
        isInternalPaymentRequest(pr2) ? pr2.actual_action : pr2.next_action,
      ),
      onChange: resolve,
      onTimeout: reject,
    });
  });

  const [iframe, form] = createInlineWindowElements({
    url: action.url,
    method: action.method,
    data: action.data,
    iframeStyle: getIframeStyles(action),
  });

  if (action.window_size === 'fullscreen') {
    document.body.appendChild(iframe);
    resolvedContainer.appendChild(form);
  } else {
    resolvedContainer.appendChild(iframe);
    resolvedContainer.appendChild(form);
  }

  form.submit();

  return Promise.any([
    callbackPromise.then((params): Promise<
    io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest
    > => {
      if (isNil(params.flow_payment_request_id)) {
        // this will reject this promise, we still have a chance with the polling below 🤞🏻
        throw new Error('handleDisplayInlineWindowAction: inline callback received without PR id');
      }
      cancelPolling();
      return isInternalPaymentRequest(pr)
        ? fetchInternalPaymentRequest(params.flow_payment_request_id)
        : fetchPaymentRequest(params.flow_payment_request_id);
    }),
    pollingPromise,
  ]).finally(() => {
    removeElement(iframe);
    removeElement(form);
  });
}
